import type {
  AutoEnhanceNotificationData,
  Notification,
} from '@/api/v4/notifications.api';
import { useNavigateTo } from '@/routes/hooks/use-navigate-to';
import { Trans, useTranslation } from 'react-i18next';

import { Inline } from '@/ui/line/line';
import { SmallText } from '@/ui/typography/widgets';
import { colors } from '@/theme/colors';
import { timeAgo } from '../utils/time-ago';

import {
  NotificationCardContainer,
  NotificationHighlight,
  NotificationIconWrapper,
} from '../components/notification-card-components';
import { useMarkNotificationAsRead } from '../hooks/use-mark-notification-as-read.mutation';
import { useUserPermissions } from '@/user/permissions/use-user-permissions';
import { PERMISSION } from '@/user/permissions/permission.type';
import { Icon } from '@/ui/icons/icon';

export const AutoEnhanceNotificationCard = ({
  notification,
}: {
  notification: Notification;
}) => {
  useTranslation('notification');
  const { data } = notification;
  const { markAsRead } = useMarkNotificationAsRead();
  const isRead = Boolean(notification?.readAt);
  const navigateTo = useNavigateTo();
  const { hasRequiredPermission } = useUserPermissions();
  const canViewPendingCompanies = hasRequiredPermission(
    PERMISSION.VIEW_PENDING_COMPANIES,
  );

  if (!data) return null;

  const { companiesCount, projectId, projectName } =
    data as AutoEnhanceNotificationData;

  const handleNotificationClick = async () => {
    if (!isRead) {
      await markAsRead({ notificationId: notification.id });
    }

    if (!projectId) return;

    navigateTo.project(
      { projectId },
      { listingsTab: canViewPendingCompanies ? 'pending-companies' : 'all' },
    );
  };

  const countPadded = String(companiesCount).padStart(2, '0');

  return (
    <NotificationCardContainer
      isRead={isRead}
      justify="space-between"
      alignItems="center"
      onClick={handleNotificationClick}
    >
      <Inline gap="12px" alignItems="center">
        <NotificationIconWrapper
          isRead={isRead}
          alignItems="center"
          justify="center"
        >
          <Icon icon="Explore" color="white" />
        </NotificationIconWrapper>
        <SmallText color={colors.gray.c13}>
          <Trans
            i18nKey="autoEnhanceBatchTitle"
            ns="notification"
            values={{ countDisplay: countPadded, projectName }}
            components={{ strong: <NotificationHighlight /> }}
          />
        </SmallText>
      </Inline>
      <SmallText>{timeAgo(notification?.createdAt)}</SmallText>
    </NotificationCardContainer>
  );
};
